import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import {
  provideStoreDevtools,
  StoreDevtoolsModule,
} from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { NgxStripeModule } from 'ngx-stripe';
import { ConfirmationService } from 'primeng/api';
import { environment } from 'src/environments/environment.local';

import { routes } from './app.routes';
import { AppTokenInterceptor, UatInterceptor } from './helpers';
import { GlobalTimelineApi } from './modules/dashboard/api/global-timeline.api';
import { PersonalTimelineApi } from './modules/dashboard/api/personal-timeline.api';
import { TimelineFeedFacade } from './modules/dashboard/facades/timeline-feed.facade';
import { LikeService } from './modules/dashboard/services/like.service';
import { FavoritesEffects } from './modules/dashboard/store/favorites/favorites.effects';
import { FollowersEffects } from './modules/dashboard/store/followers/followers.effects';
import { OrderEffects } from './modules/dashboard/store/order/order.effects';
import { PaymentMethodsEffects } from './modules/dashboard/store/payment-methods/payment-methods.effects';
import { PaymentsEffects } from './modules/dashboard/store/payments/payments.effects';
import { SubscriptionAlertsEffects } from './modules/dashboard/store/subscription-alerts/subscription-alerts.effects';
import { SubscriptionsEffects } from './modules/dashboard/store/subscriptions/subscriptions.effects';
import { TimelineSubscriptionsEffects } from './modules/dashboard/store/timeline-subscriptions/timeline-subscriptions.effect';
import { VouchersEffect } from './modules/dashboard/store/vouchers/vouchers.effects';
import { StripeAPIService, UserService } from './services';
import { GlobalCacheManager } from './services/global-cache-manager.service';
import { CountriesEffects } from './store/countries/countries.effect';
import { CountriesReducer } from './store/countries/countries.reducers';
import { MeReducer } from './store/me/me.reducer';
import { MeEffects } from './store/me/me.effects';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { CartEffects } from './modules/dashboard/store/cart/cart.effect';
import { ProductsReducer } from './store/products/products.reducers';
import { ProductsEffects } from './store/products/products.effects';
import { subscriptionsReducer } from './modules/dashboard/store/subscriptions/subscriptions.reducer';

registerLocaleData(localeFr, 'fr-FR');
registerLocaleData(localeEn, 'en-EN');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeNl, 'nl-NL');

export const PERSONAL_FEED_FACADE_TOKEN = 'personal_feed';
export const GLOBAL_FEED_FACADE_TOKEN = 'global_feed';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    CommonModule,
    BrowserModule,
    importProvidersFrom(NgbModule),
    importProvidersFrom(CarouselModule),
    provideHttpClient(withInterceptorsFromDi()),
    UserService,
    StripeAPIService,
    provideAnimations(),
    ConfirmationService,
    importProvidersFrom(DatePipe),
    importProvidersFrom(NgxStripeModule.forRoot(environment.stripe.public)),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en-EN',
        loader: {
          deps: [HttpBackend],
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
        },
      })
    ),
    {
      deps: [PersonalTimelineApi, LikeService],
      provide: PERSONAL_FEED_FACADE_TOKEN,
      useClass: TimelineFeedFacade,
    },
    {
      deps: [GlobalTimelineApi, LikeService],
      provide: GLOBAL_FEED_FACADE_TOKEN,
      useClass: TimelineFeedFacade,
    },

    importProvidersFrom(EffectsModule.forRoot([CountriesEffects, MeEffects])),

    importProvidersFrom(
      StoreModule.forRoot({
        countries: CountriesReducer,
        me: MeReducer,
        products: ProductsReducer,
      })
    ),
    importProvidersFrom(
      StoreModule.forFeature('dashboard', {
        subscriptions: subscriptionsReducer,
      })
    ),
    importProvidersFrom(
      EffectsModule.forFeature([
        FavoritesEffects,
        FollowersEffects,
        TimelineSubscriptionsEffects,
        SubscriptionAlertsEffects,
        SubscriptionsEffects,
        //Payements & products
        VouchersEffect,
        CartEffects,
        ProductsEffects,
        PaymentsEffects,
        OrderEffects,
        PaymentMethodsEffects,
      ])
    ),

    // Pipes
    DatePipe,

    // NGRX
    importProvidersFrom(
      StoreDevtoolsModule.instrument({
        logOnly: environment.production,
        maxAge: 25,
      })
    ),
    provideStoreDevtools(),
    {
      deps: [GlobalCacheManager],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: initCacheManager,
    },

    { multi: true, provide: HTTP_INTERCEPTORS, useClass: AppTokenInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: UatInterceptor },
    { provide: LOCALE_ID, useValue: 'en-EN' },
  ],
};

export function HttpLoaderFactory(httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(httpBackend, [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/storybook-assets/i18n/', suffix: '.json' },
  ]);
}

// Start cache manager at app initialization
function initCacheManager(cacheManager: GlobalCacheManager) {
  return () => {
    cacheManager.start();
  };
}
